import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Download, RefreshCw, Search, ShieldCheck } from "lucide-react"
import {
  smartoltService,
  type SmartOLTOltRow,
  type SmartOLTUnconfiguredOnuRow,
} from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import {
  SmartOLTUnconfiguredAuthorizeModal,
  type AuthorizeModalState,
} from "./SmartOLTUnconfiguredAuthorizeModal.tsx"

type PonFilter = "" | "gpon" | "epon"

function rowKey(row: SmartOLTUnconfiguredOnuRow) {
  return `${row.olt_id}-${row.board}-${row.port}-${row.onu_number}-${row.sn || ""}`
}

function exportCsv(rows: SmartOLTUnconfiguredOnuRow[]) {
  const header = ["OLT", "PON type", "Board", "Port", "PON description", "SN", "Type"]
  const lines = rows.map((row) =>
    [
      row.olt_name || row.olt_id,
      (row.pon_type || "").toUpperCase(),
      row.board,
      row.port,
      row.pon_description || "",
      row.sn || "",
      row.onu_type_name || "",
    ]
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(","),
  )
  const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = "unconfigured-onus.csv"
  link.click()
  URL.revokeObjectURL(url)
}

export function SmartOLTUnconfiguredPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [rows, setRows] = useState<SmartOLTUnconfiguredOnuRow[]>([])
  const [olts, setOlts] = useState<SmartOLTOltRow[]>([])
  const [search, setSearch] = useState("")
  const [oltFilter, setOltFilter] = useState("")
  const [boardFilter, setBoardFilter] = useState("")
  const [portFilter, setPortFilter] = useState("")
  const [ponFilter, setPonFilter] = useState<PonFilter>("")
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [auth, setAuth] = useState<AuthorizeModalState | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)

  const load = async (silent = false, sync = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      if (sync) {
        try {
          await smartoltService.runSyncNow(5)
        } catch {
          /* keep listing */
        }
      }
      const [onuRows, oltRows] = await Promise.all([
        smartoltService.getUnconfiguredOnus({ limit: 500 }),
        smartoltService.getOlts(),
      ])
      setRows(Array.isArray(onuRows) ? onuRows : [])
      setOlts(Array.isArray(oltRows) ? oltRows : [])
    } catch {
      toast.error("Failed to load unconfigured ONUs")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
    const id = setInterval(() => load(true), 30000)
    return () => clearInterval(id)
  }, [])

  const boards = useMemo(() => [...new Set(rows.map((row) => row.board).filter(Boolean))].sort(), [rows])
  const ports = useMemo(() => [...new Set(rows.map((row) => row.port).filter(Boolean))].sort(), [rows])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((row) => {
      if (oltFilter && String(row.olt_id) !== oltFilter) return false
      if (boardFilter && row.board !== boardFilter) return false
      if (portFilter && row.port !== portFilter) return false
      if (ponFilter && (row.pon_type || "").toLowerCase() !== ponFilter) return false
      if (!q) return true
      return [row.olt_name, row.olt_id, row.board, row.port, row.onu_number, row.sn, row.onu_type_name, row.pon_description, row.pon_type]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    })
  }, [rows, search, oltFilter, boardFilter, portFilter, ponFilter])

  useEffect(() => {
    setPage(1)
  }, [search, oltFilter, boardFilter, portFilter, ponFilter, pageSize])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const pageSafe = Math.min(page, totalPages)
  const paged = filtered.slice((pageSafe - 1) * pageSize, pageSafe * pageSize)

  const openAuthorize = async (onu: SmartOLTUnconfiguredOnuRow) => {
    const defaultExternalId = onu.sn || `ONU-${onu.olt_id}-${onu.board}-${onu.port}-${onu.onu_number}`
    setAuth({
      onu,
      presets: [],
      loadingPresets: true,
      selectedPreset: "",
      onuExternalId: defaultExternalId,
      authorizing: false,
    })
    try {
      const resp = await smartoltService.getAuthorizationPresets({
        olt_id: onu.olt_id,
        sn: onu.sn || undefined,
        board: onu.board || undefined,
        port: onu.port || undefined,
      })
      const list = resp?.response || resp?.presets || resp?.data || []
      const presets = Array.isArray(list) ? list : []
      setAuth((prev) =>
        prev
          ? {
              ...prev,
              presets,
              selectedPreset: String(presets[0]?.id || presets[0]?.preset_id || ""),
              loadingPresets: false,
            }
          : prev,
      )
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to fetch authorization presets")
      setAuth((prev) => (prev ? { ...prev, loadingPresets: false } : prev))
    }
  }

  const submitAuthorize = async () => {
    if (!auth || !auth.selectedPreset || !auth.onuExternalId.trim()) return
    setAuth((prev) => (prev ? { ...prev, authorizing: true } : prev))
    try {
      await smartoltService.authorizeOnuWithPreset({
        preset_id: auth.selectedPreset,
        onu_external_id: auth.onuExternalId.trim(),
      })
      await smartoltService.runCommandsNow(5)
      toast.success("Authorization queued")
      setAuth(null)
      await load(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to queue authorization")
      setAuth((prev) => (prev ? { ...prev, authorizing: false } : prev))
    }
  }

  const authorizeSelected = async () => {
    const chosen = filtered.filter((row) => selected.has(rowKey(row)))
    if (chosen.length === 0) {
      toast.error("Select at least one ONU")
      return
    }
    setBulkBusy(true)
    try {
      for (const onu of chosen) {
        const resp = await smartoltService.getAuthorizationPresets({
          olt_id: onu.olt_id,
          sn: onu.sn || undefined,
          board: onu.board || undefined,
          port: onu.port || undefined,
        })
        const list = resp?.response || resp?.presets || resp?.data || []
        const presets = Array.isArray(list) ? list : []
        const presetId = String(presets[0]?.id || presets[0]?.preset_id || "")
        if (!presetId) throw new Error(`No preset for ${onu.sn || onu.olt_id}`)
        await smartoltService.authorizeOnuWithPreset({
          preset_id: presetId,
          onu_external_id: onu.sn || `ONU-${onu.olt_id}-${onu.board}-${onu.port}-${onu.onu_number}`,
        })
      }
      await smartoltService.runCommandsNow(10)
      toast.success(`Authorization queued for ${chosen.length} ONUs`)
      setSelected(new Set())
      await load(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err.message || "Bulk authorize failed")
    } finally {
      setBulkBusy(false)
    }
  }

  const toggleRow = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const togglePage = () => {
    const keys = paged.map((row) => rowKey(row))
    const allOn = keys.every((key) => selected.has(key))
    setSelected((prev) => {
      const next = new Set(prev)
      keys.forEach((key) => (allOn ? next.delete(key) : next.add(key)))
      return next
    })
  }

  const selectClass = "h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-800 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Unconfigured ONUs/ONTs</h1>
          <p className="text-xs text-slate-500">{filtered.length} of {rows.length} discovered</p>
        </div>
      </div>

      <p className="rounded-xl border border-blue-200 bg-blue-50/80 p-3.5 sm:p-4 text-xs leading-relaxed text-blue-800 shadow-xs">
        Provisioning templates are managed per OLT. Open an OLT and use its Provisioning Templates tab, then tick discovered ONUs and use Authorize selected.
        {" "}
        <Link to={SMARTOLT_PATHS.olts} className="font-semibold underline text-blue-900">OLTs page</Link>
      </p>

      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="grid grid-cols-2 gap-2 border-b border-slate-200 bg-slate-50/80 p-3 sm:p-4 md:sticky md:top-14 md:z-10 md:flex md:flex-wrap md:items-center md:gap-2.5 md:backdrop-blur-sm">
          <div className="relative col-span-2 min-w-0 md:min-w-[180px] md:flex-1 md:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search unconfigured ONUs…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-xs"
            />
          </div>
            <select value={oltFilter} onChange={(e) => setOltFilter(e.target.value)} className={selectClass}>
              <option value="">All OLTs</option>
              {olts.map((olt) => (
                <option key={olt.olt_id} value={olt.olt_id}>
                  {olt.name || olt.olt_id}
                </option>
              ))}
            </select>
            <select value={boardFilter} onChange={(e) => setBoardFilter(e.target.value)} className={selectClass}>
              <option value="">All Boards</option>
              {boards.map((board) => (
                <option key={board} value={board}>
                  {board}
                </option>
              ))}
            </select>
            <select value={portFilter} onChange={(e) => setPortFilter(e.target.value)} className={selectClass}>
              <option value="">All Ports</option>
              {ports.map((port) => (
                <option key={port} value={port}>
                  {port}
                </option>
              ))}
            </select>
          <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
            {(["gpon", "epon"] as Exclude<PonFilter, "">[]).map((value) => (
              <button key={value} type="button" onClick={() => setPonFilter(ponFilter === value ? "" : value)}
                className={`h-9 px-3 text-xs font-semibold ${
                  ponFilter === value ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}>
                {value.toUpperCase()}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => exportCsv(filtered)} disabled={filtered.length === 0}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-50">
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
          <button type="button" onClick={authorizeSelected} disabled={bulkBusy || selected.size === 0}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 shadow-xs disabled:opacity-50 transition-colors">
            <ShieldCheck className="h-3.5 w-3.5" />
            Authorize selected
          </button>
          <button type="button" onClick={() => load(false, true)} disabled={refreshing}
            className="col-span-2 inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white shadow-xs hover:bg-blue-700 disabled:opacity-50 md:col-span-1 md:ml-auto">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="md:hidden">
          <div className="divide-y divide-slate-100 bg-white">
            {paged.map((row) => {
              const key = rowKey(row)
              const canAuthorize = !row.actions || row.actions.includes("authorize")
              return (
                <div key={key} className="p-3">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selected.has(key)}
                      onChange={() => toggleRow(key)}
                      aria-label={`Select ${row.sn || key}`}
                      className="mt-1 h-4 w-4 rounded border-slate-300 bg-white accent-blue-600"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate font-mono text-sm font-bold text-slate-900">{row.sn || "-"}</div>
                          <div className="mt-0.5 truncate text-xs text-slate-500">{row.olt_name || row.olt_id}</div>
                        </div>
                        <span className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase ${
                          row.pon_type === "gpon"
                            ? "bg-blue-50 text-blue-700 border border-blue-200"
                            : "bg-purple-50 text-purple-700 border border-purple-200"
                        }`}>{row.pon_type || "-"}</span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                          <div className="text-[10px] font-semibold uppercase text-slate-500">Board / Port</div>
                          <div className="mt-0.5 font-mono font-semibold text-slate-800">{row.board || "-"}/{row.port || "-"}</div>
                        </div>
                        <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                          <div className="text-[10px] font-semibold uppercase text-slate-500">ONU No.</div>
                          <div className="mt-0.5 font-mono font-semibold text-slate-800">{row.onu_number || "-"}</div>
                        </div>
                        <div className="col-span-2 rounded-lg bg-slate-50 px-2.5 py-2">
                          <div className="text-[10px] font-semibold uppercase text-slate-500">PON Description</div>
                          <div className="mt-0.5 truncate font-semibold text-slate-800">{row.pon_description || "-"}</div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <span className="truncate text-xs text-slate-500">{row.onu_type_name || "No ONU type"}</span>
                        <button type="button" onClick={() => openAuthorize(row)} disabled={!canAuthorize}
                          className="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 shadow-xs disabled:opacity-50 transition-colors">
                          <ShieldCheck className="h-3.5 w-3.5" />
                          Authorize
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          {!loading && filtered.length === 0 && (
            <p className="px-3 py-10 text-center text-sm text-slate-500">
              {rows.length === 0 ? "No data available in table" : "No rows match the current filters."}
            </p>
          )}
          {loading && <p className="px-3 py-10 text-center text-sm text-slate-500">Loading unconfigured ONUs...</p>}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-xs">
            <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
              <tr>
                <th className="px-3 py-2.5 text-left">
                  <input type="checkbox" aria-label="Select page" checked={paged.length > 0 && paged.every((row) => selected.has(rowKey(row)))} onChange={togglePage}
                    className="h-3.5 w-3.5 rounded border-slate-300 bg-white accent-blue-600" />
                </th>
                <th className="px-3 py-2.5 text-left font-semibold">OLT</th>
                <th className="px-3 py-2.5 text-left font-semibold">PON type</th>
                <th className="px-3 py-2.5 text-left font-semibold">Board</th>
                <th className="px-3 py-2.5 text-left font-semibold">Port</th>
                <th className="px-3 py-2.5 text-left font-semibold">PON description</th>
                <th className="px-3 py-2.5 text-left font-semibold">SN</th>
                <th className="px-3 py-2.5 text-left font-semibold">Type</th>
                <th className="px-3 py-2.5 text-right font-semibold">Authorize</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {paged.map((row) => {
                const key = rowKey(row)
                const canAuthorize = !row.actions || row.actions.includes("authorize")
                return (
                  <tr key={key} className="transition-colors hover:bg-slate-50/80">
                    <td className="px-3 py-2.5">
                      <input type="checkbox" checked={selected.has(key)} onChange={() => toggleRow(key)}
                        aria-label={`Select ${row.sn || key}`}
                        className="h-3.5 w-3.5 rounded border-slate-300 bg-white accent-blue-600" />
                    </td>
                    <td className="px-3 py-2.5 text-slate-800 font-medium">{row.olt_name || row.olt_id}</td>
                    <td className="px-3 py-2.5">
                      <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                        row.pon_type === "gpon"
                          ? "bg-blue-50 text-blue-700 border border-blue-200"
                          : "bg-purple-50 text-purple-700 border border-purple-200"
                      }`}>{row.pon_type || "—"}</span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-slate-600">{row.board || "-"}</td>
                    <td className="px-3 py-2.5 font-mono text-slate-600">{row.port || "-"}</td>
                    <td className="px-3 py-2.5 text-slate-600">{row.pon_description || "-"}</td>
                    <td className="px-3 py-2.5 font-mono font-semibold text-slate-900">{row.sn || "-"}</td>
                    <td className="px-3 py-2.5 text-slate-600">{row.onu_type_name || "-"}</td>
                    <td className="px-3 py-2.5 text-right">
                      <button type="button" onClick={() => openAuthorize(row)} disabled={!canAuthorize}
                        className="inline-flex h-8 items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 shadow-xs disabled:opacity-50 transition-colors">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Authorize
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {!loading && filtered.length === 0 && (
            <p className="px-3 py-10 text-center text-sm text-slate-500">
              {rows.length === 0 ? "No data available in table" : "No rows match the current filters."}
            </p>
          )}
          {loading && <p className="px-3 py-10 text-center text-sm text-slate-500">Loading unconfigured ONUs…</p>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/80 px-4 py-3 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <select aria-label="Rows per page" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-slate-700 shadow-xs">
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span>
              {filtered.length === 0 ? "Showing no records" : `Showing ${(pageSafe - 1) * pageSize + 1} to ${Math.min(pageSafe * pageSize, filtered.length)} of ${filtered.length} records`}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="h-8 rounded-md border border-slate-200 bg-white px-3 text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-40 font-medium">Prev</button>
            <button type="button" disabled={pageSafe >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="h-8 rounded-md border border-slate-200 bg-white px-3 text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-40 font-medium">Next</button>
          </div>
        </div>
      </div>

      {auth ? (
        <SmartOLTUnconfiguredAuthorizeModal
          state={auth}
          onClose={() => setAuth(null)}
          onChange={(next) => setAuth((prev) => (prev ? { ...prev, ...next } : prev))}
          onSubmit={submitAuthorize}
        />
      ) : null}
    </div>
  )
}
